import gsap from 'gsap';
import {
  AnimationMixer,
  CanvasTexture,
  LoopRepeat,
  Matrix3,
  NearestFilter,
  Object3D,
  sRGBEncoding,
  StaticReadUsage,
  Vector2,
  Vector3,
} from 'three';
import correctUvUnwrap from '../../Tools/CorrectUvUnwrap';
import { appMixin } from '../../utils/appMixin';

import * as equipments from '../Helpers/Kong/Equipments';
import * as animations from '../Helpers/Kong/Animations';
import * as materials from '../Helpers/Kong/Materials';

import { KongMaterial } from '../Materials/Kong/material';
export class Kong extends appMixin(Object3D) {
  constructor(app) {
    super(app);

    this.materials = [];

    this.applyHelpers(equipments);
    this.applyHelpers(animations);
    this.applyHelpers(materials);

    this.rotation.y = Math.PI * 1.5;
    this.visible = false;
    this._animation = null;
    this._animationPlaying = true;
    this._actions = [];
    this._actionsAvailable = false;
    this.first = true;
    this.mixers = new Set();

    this.instanciedMaterials = new Map();

    this.scale.set(0.021, 0.021, 0.021);

    this._initialConfig = {
      uColorInfluence: 0.05,
      uSunPosition: new Vector3(-0.03, 0.82, 0.4),
      uSunIntensity: 0.3,
      uAmbientIntensity: 0.8,
      uDiffuseSaturation: 1.09,
      uDiffuseContrast: 0.09,
      uDiffuseIntensity: 1.4,
      uShadowIntensity: 0.3,
      uEmissiveSaturation: 0,
      uEmissiveIntensity: 0.3,
      uEmissiveAddition: 1,
      uPostEmissiveSaturation: 0.94,
      uPostEmissiveIntensity: 1.5,
      uPostEmissiveAddition: 0.26,
      uAoIntensity: 0.45,
    };

    this._initialArmorConfig = {
      uColorInfluence: 0.05,
      uSunPosition: new Vector3(-0.03, 0.82, 0.4),
      uSunIntensity: 0.3,
      uAmbientIntensity: 0.7,
      uDiffuseSaturation: 1.1,
      uDiffuseContrast: 0.09,
      uDiffuseIntensity: 1.4,
      uShadowIntensity: 0.3,
      uEmissiveSaturation: 0.3,
      uEmissiveIntensity: 6,
      uEmissiveAddition: 1,
      uPostEmissiveSaturation: 1.8,
      uPostEmissiveIntensity: 1.3,
      uPostEmissiveAddition: 0.3,
      uAoIntensity: 0.5,
    };

    this.commonUniforms = {
      uColor: this.app.uniforms.uColor,
      uVisiblity: { value: 0 },
      uOnlyEmissive: { value: 0 },

      uColorInfluence: { value: this._initialConfig.uColorInfluence },
      uSunPosition: { value: this._initialConfig.uSunPosition },
      uSunIntensity: { value: this._initialConfig.uSunIntensity },
      uAmbientIntensity: { value: this._initialConfig.uAmbientIntensity },
      uDiffuseSaturation: { value: this._initialConfig.uDiffuseSaturation },
      uDiffuseContrast: { value: this._initialConfig.uDiffuseContrast },
      uDiffuseIntensity: { value: this._initialConfig.uDiffuseIntensity },
      uShadowIntensity: { value: this._initialConfig.uShadowIntensity },
      uEmissiveSaturation: { value: this._initialConfig.uEmissiveSaturation },
      uEmissiveIntensity: { value: this._initialConfig.uEmissiveIntensity },
      uEmissiveAddition: { value: this._initialConfig.uEmissiveAddition },
      uPostEmissiveSaturation: { value: this._initialConfig.uPostEmissiveSaturation },
      uPostEmissiveIntensity: { value: this._initialConfig.uPostEmissiveIntensity },
      uPostEmissiveAddition: { value: this._initialConfig.uPostEmissiveAddition },
      uAoIntensity: { value: this._initialConfig.uAoIntensity },
    };

    this.commonArmorUniforms = {
      uColor: this.app.uniforms.uColor,
      uVisiblity: { value: 0 },
      uOnlyEmissive: { value: 0 },

      uColorInfluence: { value: this._initialArmorConfig.uColorInfluence },
      uSunPosition: { value: this._initialArmorConfig.uSunPosition },
      uSunIntensity: { value: this._initialArmorConfig.uSunIntensity },
      uAmbientIntensity: { value: this._initialArmorConfig.uAmbientIntensity },
      uDiffuseSaturation: { value: this._initialArmorConfig.uDiffuseSaturation },
      uDiffuseContrast: { value: this._initialArmorConfig.uDiffuseContrast },
      uDiffuseIntensity: { value: this._initialArmorConfig.uDiffuseIntensity },
      uShadowIntensity: { value: this._initialArmorConfig.uShadowIntensity },
      uEmissiveSaturation: { value: this._initialArmorConfig.uEmissiveSaturation },
      uEmissiveIntensity: { value: this._initialArmorConfig.uEmissiveIntensity },
      uEmissiveAddition: { value: this._initialArmorConfig.uEmissiveAddition },
      uPostEmissiveSaturation: { value: this._initialArmorConfig.uPostEmissiveSaturation },
      uPostEmissiveIntensity: { value: this._initialArmorConfig.uPostEmissiveIntensity },
      uPostEmissiveAddition: { value: this._initialArmorConfig.uPostEmissiveAddition },
      uAoIntensity: { value: this._initialArmorConfig.uAoIntensity },
    };

    this.additionnalParts = new Map();
    this.subObjectsMap = new Map();

    this.updateModel(this.app.core.loader.getModel('kong'));
  }

  applyHelpers(o) {
    Object.entries(o).forEach(([key, func]) => {
      this[key] = func;
    });
  }

  dispose(object) {
    object.traverse((o) => {
      if (o.isMesh) {
        o.material.uniforms.tMap.value.dispose();
        o.material.uniforms.tEmissiveMap.value.dispose();
      }
    });
  }

  disposeKong = () => {
    this.materials.forEach((material) => {
      material.uniforms.tMap.value.dispose();
      material.uniforms.tEmissiveMap.value.dispose();
      material.dispose();
    });
    this.subObjectsMap.clear();

    this.traverse((o) => {
      if (o.isMesh) o.geometry?.dispose();
    });

    this._actions = [];
    this._actionsAvailable = false;
    this.clear();
  };

  updateModel = (object) => {
    this.disposeKong();

    this.model = object;

    this.model.traverse((e) => {
      this.subObjectsMap.set(e.name, e);
    });

    this.mergeMaterials(object, this.commonUniforms);
    correctUvUnwrap(object);
    this.computeAo(object);

    this.add(object);

    this.mixer = new AnimationMixer(object);
    this.mixers.add(this.mixer);
    this._actions = this.updateAnimations(object, this.mixer);

    if (this._animation != null) this.setAnimation(this._animation);
  };

  applyConfig = (config = null) => {
    if (config === null) return;
    Object.keys(this._initialConfig).forEach((key) => {
      this.commonUniforms[key].value = this._initialConfig[key];
    });
    this.app.webgl.postProcessing.uniforms.uEmissiveIntensity.value =
      this.app.webgl.postProcessing.defaultEmissiveIntensity;

    if (config?.kong?.attributes) {
      const attributes = config.kong.attributes.map((a) => a.value);

      if (attributes.includes('Shrine Seeker')) {
        this.commonUniforms.uEmissiveIntensity.value = 2;
        this.app.webgl.postProcessing.uniforms.uEmissiveIntensity.value = 0.6;
      } else if (attributes.includes('Plutonium')) {
        this.commonUniforms.uEmissiveSaturation.value = 1;
        this.app.webgl.postProcessing.uniforms.uEmissiveIntensity.value = 0.7;
      } else if (attributes.includes('Magma')) {
        this.app.webgl.postProcessing.uniforms.uEmissiveIntensity.value = 0.5;
      }
    }
  };

  onTick = ({ dt }) => {
    if (this._animationPlaying) this.mixers.forEach((m) => m.update(dt * 0.0008));
    if (!this.firstRenderDone) this.afterFirstRender();
  };

  open() {
    this.tl?.kill();
    this.tl = gsap.timeline();

    this.tl.set(this, { visible: true });
    this.tl.to(
      [this.commonUniforms.uVisiblity, this.commonArmorUniforms.uVisiblity],
      { value: 1, duration: 5, ease: 'power3.out' },
      0
    );
    this.tl.to(this.rotation, { y: 0, ease: 'power3.out', duration: 2 }, 0);

    return this.tl;
  }

  close() {
    this.tl?.kill();
    this.tl = gsap.timeline();

    this.tl.to(this.rotation, { y: Math.PI * 1.5, ease: 'power2.in', duration: 1.5 }, 0);
    this.tl.to(
      [this.commonUniforms.uVisiblity, this.commonArmorUniforms.uVisiblity],
      { value: 0, duration: 1.5, ease: 'sine.in' },
      0
    );
    this.tl.set(this, { visible: false });

    return this.tl;
  }

  get onlyEmissive() {
    return this.commonUniforms.uOnlyEmissive.value > 0.5;
  }

  set onlyEmissive(value) {
    this.commonArmorUniforms.uOnlyEmissive.value = this.commonUniforms.uOnlyEmissive.value = value
      ? 1
      : 0;
  }
}
